/**
 * Table Atom
 * @layer atom
 * @package @plerity/ui-core
 */

import { clsx } from "clsx";
import { type CSSProperties, forwardRef } from "react";
import { OverflowRegion } from "../OverflowRegion/OverflowRegion";
import type {
  TableCellProps,
  TableCellWrap,
  TableHeadCellProps,
  TableHeaderProps,
  TableProps,
  TableRowProps,
} from "./Table.types";

export const Table = forwardRef<HTMLTableElement, TableProps>(({ className, children, ...props }, ref) => {
  return (
    // Keyboard-reachable while it overflows (WCAG 2.1.1); plain div when it fits.
    <OverflowRegion label={props["aria-label"] ?? "Table"} className="w-full">
      <table ref={ref} className={clsx("w-full border-collapse text-sm", className)} {...props}>
        {children}
      </table>
    </OverflowRegion>
  );
});

Table.displayName = "Table";

export const TableHeader = forwardRef<HTMLTableSectionElement, TableHeaderProps>(
  ({ className, children, ...props }, ref) => {
    return (
      <thead ref={ref} className={clsx("border-b border-gray-200 dark:border-gray-700", className)} {...props}>
        {children}
      </thead>
    );
  }
);

TableHeader.displayName = "TableHeader";

export const TableRow = forwardRef<HTMLTableRowElement, TableRowProps>(
  ({ interactive = false, className, children, ...props }, ref) => {
    return (
      <tr
        ref={ref}
        className={clsx(
          // Literal gray ramp — do NOT use `border-[var(--color-border-default)]` or
          // `hover:bg-surface-subtle`. Those emit `var(--color-*)` with NO fallback
          // and are stripped from the React SPA apps' Tailwind v4 build.
          "border-b border-gray-200 dark:border-gray-700",
          interactive && "cursor-pointer hover:bg-gray-100 transition-colors duration-150 dark:hover:bg-gray-700",
          className
        )}
        {...props}
      >
        {children}
      </tr>
    );
  }
);

TableRow.displayName = "TableRow";

/**
 * Returns the Tailwind utility classes for a given wrap mode. Shared by
 * `<TableCell>` and `<TableHeadCell>` so both cell variants behave the same.
 *
 *   `'normal'`   → wraps onto multiple lines, breaks long words.
 *   `'truncate'` → single line, ellipsis (caller usually pairs with maxWidth).
 *   `'nowrap'`   → legacy single-line-no-ellipsis behavior.
 */
function wrapClasses(wrap: TableCellWrap): string {
  if (wrap === "truncate") return "truncate";
  if (wrap === "nowrap") return "whitespace-nowrap";
  // `break-words` lets long unbroken tokens (URLs, IDs) wrap inside the cell
  // rather than blowing the table out horizontally.
  return "whitespace-normal break-words";
}

export const TableCell = forwardRef<HTMLTableCellElement, TableCellProps>(
  ({ wrap = "normal", maxWidth, style, className, children, ...props }, ref) => {
    const mergedStyle: CSSProperties | undefined = maxWidth ? { maxWidth, ...style } : style;
    return (
      <td
        ref={ref}
        style={mergedStyle}
        className={clsx("py-2.5 px-4 text-sm text-gray-900 align-top dark:text-gray-100", wrapClasses(wrap), className)}
        {...props}
      >
        {children}
      </td>
    );
  }
);

TableCell.displayName = "TableCell";

export const TableHeadCell = forwardRef<HTMLTableCellElement, TableHeadCellProps>(
  (
    { sortable = false, sorted = false, onSort, wrap = "normal", maxWidth, style, className, children, ...props },
    ref
  ) => {
    const mergedStyle: CSSProperties | undefined = maxWidth ? { maxWidth, ...style } : style;
    // A sortable header must be operable by keyboard (WCAG 2.1.1) — Enter/Space
    // trigger the same `onSort` as a pointer click. `aria-sort` (WCAG 1.3.1)
    // exposes the current sort direction to assistive tech.
    const handleKeyDown =
      sortable && onSort
        ? (e: import("react").KeyboardEvent<HTMLTableCellElement>) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              onSort();
            }
          }
        : undefined;
    const ariaSort: import("react").AriaAttributes["aria-sort"] = sortable
      ? sorted === "asc"
        ? "ascending"
        : sorted === "desc"
          ? "descending"
          : "none"
      : undefined;
    return (
      <th
        ref={ref}
        scope="col"
        onClick={sortable ? onSort : undefined}
        onKeyDown={handleKeyDown}
        tabIndex={sortable ? 0 : undefined}
        aria-sort={ariaSort}
        style={mergedStyle}
        className={clsx(
          "py-2 px-4",
          // Muted header label: gray-500 is 3.0:1 on a dark card, so it needs its
          // dark: pair (gray-400 = 5.8:1). Hover goes to the primary text stop.
          "text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider text-left align-top",
          wrapClasses(wrap),
          sortable &&
            "cursor-pointer select-none hover:text-gray-900 dark:hover:text-gray-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-brand-focus",
          className
        )}
        {...props}
      >
        <span className="inline-flex items-center gap-1">
          {/* `min-w-0` lets the inner truncate / wrap actually engage when
              the header content is wider than the cell. */}
          <span
            className={clsx("min-w-0", wrap === "truncate" && "truncate")}
            title={wrap === "truncate" && typeof children === "string" ? children : undefined}
          >
            {children}
          </span>
          {sortable && sorted && <SortIcon direction={sorted} />}
        </span>
      </th>
    );
  }
);

TableHeadCell.displayName = "TableHeadCell";

function SortIcon({ direction }: { direction: "asc" | "desc" }) {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={clsx("text-gray-900 dark:text-gray-100", direction === "desc" && "rotate-180")}
      aria-hidden="true"
    >
      <path d="M12 5v14M5 12l7-7 7 7" />
    </svg>
  );
}
